import {
  Box,
  MenuItem,
  Select,
  SxProps,
  Typography,
} from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import * as Time from "@outschool/time";
import { dayjs } from "@outschool/time";
import React, { useEffect, useState } from "react";

import * as Learner from "../../../shared/Learner";

export function DateOfBirthPicker({
  birthMonth,
  birthYear,
  onChange,
  sx,
  selectSx,
  highlightErrors = false,
  labels = false,
}: {
  birthMonth: number | null | undefined;
  birthYear: number | null | undefined;
  onChange: (
    month: number | null,
    year: number | null,
    age: number | null,
  ) => void;
  sx?: SxProps;
  selectSx?: SxProps;
  highlightErrors?: boolean;
  labels?: boolean;
}) {
  const { t } = useTranslation(
    "client\\components\\learner\\DateOfBirthPicker",
  );
  const [month, setMonth] = useState(birthMonth || null);
  const [year, setYear] = useState(birthYear || null);

  useEffect(() => {
    let age = null;
    if (!month || !year) {
      onChange(null, null, null);
    }
    // Assume the 1st of the month for age calculation purposes
    // Calculate DoB using UTC to avoid timezone issues
    const dateOfBirthForAge = dayjs.utc(`${year}-${month}-1`, "YYYY-M-D");
    // @ts-ignore TS(2322): Type 'number' is not assignable to type 'null'.
    age = dayjs().diff(dateOfBirthForAge, "years");
    onChange(month, year, age);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year]);

  const selectStyles = {
    marginBottom: "0.5em",
    width: "115px",
    ...selectSx,
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          gap: "0.5em",
          ...sx,
        }}
      >
        <Box sx={{ position: "relative", width: selectStyles.width }}>
          <Select
            size="small"
            sx={selectStyles}
            value={month}
            onChange={e => setMonth(parseInt(e.target.value as string))}
            error={highlightErrors && !month}
            displayEmpty
            renderValue={selected => {
              if (selected === null) {
                return (
                  <Box component="span" sx={{ color: "grey.400" }}>
                    {t("Month")}
                  </Box>
                );
              }
              return Time.MONTHS[selected - 1];
            }}
            {...(labels ? { label: t("Birth Month") } : {})}
          >
            {Time.MONTHS.map((monthName, index) => (
              <MenuItem value={index + 1} key={index}>
                {monthName}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={{ position: "relative", width: selectStyles.width }}>
          <Select
            size="small"
            sx={selectStyles}
            value={year}
            onChange={e => setYear(parseInt(e.target.value as string))}
            error={highlightErrors && !year}
            displayEmpty
            renderValue={selected => {
              if (selected === null) {
                return (
                  <Box component="span" sx={{ color: "grey.400" }}>
                    {t("Year")}
                  </Box>
                );
              }
              return selected;
            }}
            {...(labels ? { label: t("Birth Year") } : {})}
          >
            {Learner.yearOptionsExpanded.map(yearOption => (
              <MenuItem value={yearOption} key={yearOption}>
                {yearOption}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>
      {highlightErrors && (!month || !year) && (
        <Typography
          sx={{
            color: "red",
            marginTop: "0.5em",
            fontWeight: 500,
          }}
        >
          {t("Please select a month and year for date of birth")}
        </Typography>
      )}
    </Box>
  );
}
